/**
 * Android Push Notification Service using Firebase Cloud Messaging (FCM)
 * Handles registration, receiving notifications, and notification display
 */

import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import api from './api';
import { getStorageJSON, setStorageJSON } from '../utils/capacitorStorage';

class NotificationService {
  constructor() {
    this.fcmToken = null;
    this.isInitialized = false;
    this.listeners = [];
  }

  /**
   * Initialize push notifications
   * Works in both native app (FCM) and web browser (Browser Notifications)
   */
  async initialize() {
    const isNative = Capacitor.isNativePlatform();
    
    if (this.isInitialized) {
      console.log('NotificationService: Already initialized');
      return;
    }

    try {
      if (isNative) {
        // Native mode: Use Capacitor Push Notifications (FCM)
        await this.initializeNative();
      } else {
        // Web mode: Use Browser Notifications API
        await this.initializeWeb();
      }

      this.isInitialized = true;
      console.log('NotificationService: Initialized successfully');
    } catch (error) {
      // Don't crash the app if notifications fail to initialize
      // This is especially important if Firebase is not configured
      const errorMessage = error?.message || error?.toString() || '';
      if (errorMessage.includes('Firebase') || errorMessage.includes('FirebaseApp') || 
          errorMessage.includes('not initialized') || errorMessage.includes('IllegalStateException')) {
        console.warn('NotificationService: Firebase not configured. Push notifications disabled.');
        console.warn('NotificationService: App will continue without push notifications.');
        // Mark as initialized anyway so we don't keep trying
        this.isInitialized = true;
      } else {
        console.error('NotificationService: Initialization failed:', error);
        // Still mark as initialized to prevent retry loops
        this.isInitialized = true;
      }
    }
  }

  /**
   * Initialize native push notifications (FCM)
   * Note: This will fail gracefully if Firebase is not configured
   */
  async initializeNative() {
    // IMPORTANT: Skip push notifications entirely if Firebase might not be configured
    // This prevents native crashes when Firebase isn't initialized
    // The plugin will try to access Firebase during registration, causing a crash
    console.warn('NotificationService: Push notifications require Firebase configuration.');
    console.warn('NotificationService: To enable push notifications, add google-services.json to android/app/');
    console.warn('NotificationService: Skipping push notification initialization to prevent crashes.');
    
    // Mark as initialized (but disabled) so we don't keep trying
    this.isInitialized = true;
    return;
    
    /* 
     * COMMENTED OUT: Push notifications initialization
     * Uncomment and configure Firebase (google-services.json) to enable
     * 
    try {
      // Dynamically import PushNotifications to avoid errors in web mode
      const { PushNotifications } = await import('@capacitor/push-notifications');
      
      console.log('NotificationService: Initializing FCM...');

      // Request permission
      let permStatus = await PushNotifications.checkPermissions();
      
      if (permStatus.receive === 'prompt') {
        permStatus = await PushNotifications.requestPermissions();
      }

      if (permStatus.receive !== 'granted') {
        console.warn('NotificationService: Push notification permission denied');
        return;
      }

      // Register for push notifications
      // This will crash if Firebase is not initialized (no google-services.json)
      await PushNotifications.register();

      // Set up listeners (pass PushNotifications instance)
      this.setupListeners(PushNotifications);
    } catch (error) {
      const errorMessage = error?.message || error?.toString() || '';
      if (errorMessage.includes('Firebase') || errorMessage.includes('FirebaseApp') || 
          errorMessage.includes('not initialized') || errorMessage.includes('IllegalStateException')) {
        console.warn('NotificationService: Firebase not configured. Push notifications disabled.');
        console.warn('NotificationService: To enable push notifications, add google-services.json to android/app/');
        return;
      }
      console.error('NotificationService: Error initializing native push notifications:', error);
      throw error;
    }
    */
  }

  /**
   * Initialize web browser notifications
   */
  async initializeWeb() {
    console.log('NotificationService: Initializing browser notifications...');

    // Check if browser supports notifications
    if (!('Notification' in window)) {
      console.warn('NotificationService: Browser does not support notifications');
      return;
    }

    // Request permission
    if (Notification.permission === 'default') {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        console.warn('NotificationService: Browser notification permission denied');
        return;
      }
    } else if (Notification.permission !== 'granted') {
      console.warn('NotificationService: Browser notification permission denied');
      return;
    }

    // Set up web notification listeners
    this.setupWebListeners();

    // For web mode, we can still register with backend for server-sent notifications
    // The backend can send notifications via WebSocket or Server-Sent Events
    console.log('NotificationService: Browser notifications ready');
  }

  /**
   * Set up web notification listeners
   */
  setupWebListeners() {
    // Listen for service worker messages (if using service worker)
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', (event) => {
        if (event.data && event.data.type === 'NOTIFICATION') {
          this.showBrowserNotification(event.data.notification);
          this.notifyListeners('received', event.data.notification);
        }
      });
    }

    // Note: notificationclick events are handled in service worker
    // This is set up in the service worker file, not here
    // The showBrowserNotification method handles click events for regular notifications
  }

  /**
   * Set up notification listeners
   */
  setupListeners(PushNotifications) {
    // Token registration
    PushNotifications.addListener('registration', async (token) => {
      console.log('FCM Registration Token:', token.value);
      this.fcmToken = token.value;
      
      // Store token
      await setStorageJSON('fcmToken', token.value);
      
      // Send token to backend
      await this.sendTokenToBackend(token.value);
      
      // Notify listeners
      this.notifyListeners('token', token.value);
    });

    // Registration error
    PushNotifications.addListener('registrationError', (error) => {
      console.error('FCM Registration Error:', error);
      this.notifyListeners('error', error);
    });

    // Notification received (foreground)
    PushNotifications.addListener('pushNotificationReceived', (notification) => {
      console.log('Push notification received:', notification);
      this.notifyListeners('received', notification);
      
      // Show local notification if app is in foreground
      this.showLocalNotification(notification);
    });

    // Notification action performed (tapped)
    PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
      console.log('Push notification action:', action);
      this.notifyListeners('action', action);
      
      // Handle navigation if needed
      if (action.notification.data?.url) {
        // Navigate to URL if provided
        window.location.href = action.notification.data.url;
      }
    });

    // App state changes
    App.addListener('appStateChange', ({ isActive }) => {
      if (isActive && this.fcmToken) {
        // Re-register token when app comes to foreground
        this.sendTokenToBackend(this.fcmToken).catch(console.error);
      }
    });
  }

  /**
   * Send FCM token to backend
   */
  async sendTokenToBackend(token) {
    try {
      // Get operator info
      const operatorData = await getStorageJSON('operatorData');
      
      if (!operatorData) {
        console.warn('NotificationService: No operator data, skipping token registration');
        return;
      }

      // Send to backend
      await api.post('/operators/fcm-token/', {
        fcm_token: token,
        operator_code: operatorData.operator_code,
        device_info: {
          platform: Capacitor.getPlatform(),
          app_version: this.getAppVersion(),
        },
      });

      console.log('NotificationService: Token sent to backend successfully');
    } catch (error) {
      console.error('NotificationService: Failed to send token to backend:', error);
    }
  }

  /**
   * Show local notification (for foreground notifications)
   * Works in both native and web modes
   */
  async showLocalNotification(notification) {
    try {
      const isNative = Capacitor.isNativePlatform();
      
      if (isNative) {
        // In native mode, Capacitor handles this automatically
        // This is mainly for web mode
        this.showBrowserNotification(notification);
      } else {
        this.showBrowserNotification(notification);
      }
    } catch (error) {
      console.error('Error showing local notification:', error);
    }
  }

  /**
   * Show browser notification
   */
  showBrowserNotification(notification) {
    if ('Notification' in window && Notification.permission === 'granted') {
      const notif = new Notification(notification.title || 'JCAAPL', {
        body: notification.body,
        icon: '/logo192.png',
        badge: '/logo192.png',
        tag: notification.id?.toString(),
        data: notification.data,
      });

      // Handle click
      notif.onclick = (event) => {
        event.preventDefault();
        this.notifyListeners('action', {
          notification: notification,
          actionId: 'click',
        });

        if (notification.data?.url) {
          window.open(notification.data.url, '_blank');
        }
        
        window.focus();
      };
    }
  }

  /**
   * Add notification listener
   */
  addListener(callback) {
    this.listeners.push(callback);
    return () => {
      this.listeners = this.listeners.filter(l => l !== callback);
    };
  }

  /**
   * Notify all listeners
   */
  notifyListeners(event, data) {
    this.listeners.forEach(callback => {
      try {
        callback(event, data);
      } catch (error) {
        console.error('Error in notification listener:', error);
      }
    });
  }

  /**
   * Get FCM token
   */
  async getToken() {
    if (!this.fcmToken) {
      const stored = await getStorageJSON('fcmToken');
      this.fcmToken = stored;
    }
    return this.fcmToken;
  }

  /**
   * Get app version
   */
  getAppVersion() {
    // This would typically come from package.json or Capacitor config
    return '1.0.0';
  }

  /**
   * Cleanup
   */
  destroy() {
    this.listeners = [];
  }
}

// Singleton instance
const notificationService = new NotificationService();

export default notificationService;

